/**
 * F43-FC §6.2 — Bookings tab panel on the admin platform-user detail page.
 *
 * REUSE-only: renders the same row list TabRows did, but with a per-row
 * "Force transition" button (gated `bookings:override`). The row id IS
 * `bookings.id` (admin-user.service getBookings), so we lazy-fetch the full
 * `BookingDetail` via getAdminBooking before mounting the shared
 * ForceTransitionDialog, and route confirm through forceTransitionAdminBooking.
 *
 * `onConfirm` re-throws on failure so the dialog stays open (§6.2 gotcha).
 */
import { useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Badge, Button, Card, EmptyState, Skeleton, useToast } from '@bravemove/ui-web';
import type { BookingDetail, ForceTransitionInput } from '@bravemove/types';
import { getAdminBooking, forceTransitionAdminBooking } from '../../api/adminBookings';
import { ForceTransitionDialog } from '../ForceTransitionDialog';
import type { AdminUserTabRow } from '../../api/adminUsers';

export function BookingsAdminPanel({
  rows,
  canForceTransition,
  onAfterTransition,
}: {
  readonly rows: readonly AdminUserTabRow[] | undefined;
  readonly canForceTransition: boolean;
  readonly onAfterTransition: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  // The booking whose detail was lazily fetched + is now in the open dialog.
  const [active, setActive] = useState<BookingDetail | null>(null);
  // The row currently fetching its detail (disables its button + shows a spinner).
  const [fetchingId, setFetchingId] = useState<string | null>(null);

  if (rows === undefined) return <Skeleton className="h-24 w-full rounded-lg" />;
  if (!Array.isArray(rows) || rows.length === 0) {
    return <EmptyState title={t('admin:f43.empty.generic')} />;
  }

  const openForceTransition = async (rowId: string) => {
    setFetchingId(rowId);
    try {
      const detail = await getAdminBooking(rowId);
      if (detail === null) {
        toast.push({
          tone: 'danger',
          title: t('booking:admin.detail.notFound', { defaultValue: 'Booking not found.' }),
        });
        return;
      }
      setActive(detail);
    } catch {
      toast.push({
        tone: 'danger',
        title: t('booking:admin.detail.loadError', { defaultValue: 'Failed to load booking.' }),
      });
    } finally {
      setFetchingId(null);
    }
  };

  const handleConfirm = async (input: ForceTransitionInput) => {
    if (!active) return;
    try {
      await forceTransitionAdminBooking(active.id, input);
      toast.push({ tone: 'success', title: t('booking:admin.force_transition.success') });
      setActive(null);
      onAfterTransition();
    } catch {
      // Re-throw so the shared dialog stays OPEN (it only calls onClose after a
      // resolved onConfirm). §6.2 gotcha.
      toast.push({ tone: 'danger', title: t('booking:admin.force_transition.error') });
      throw new Error('FORCE_TRANSITION_FAILED');
    }
  };

  return (
    <>
      <Card>
        <ul className="divide-y divide-outline" data-testid="f43fc-bookings-panel">
          {rows.map((r) => (
            <li
              key={r.id}
              className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5"
              data-testid={`f43fc-booking-row-${r.id}`}
            >
              <div className="min-w-0">
                <p className="text-[13px] text-ink truncate">{r.title}</p>
                {r.subtitle ? (
                  <p className="text-[12px] text-ink-3 truncate">{r.subtitle}</p>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                {r.status ? (
                  <Badge tone="neutral" size="sm">
                    {r.status}
                  </Badge>
                ) : null}
                {canForceTransition ? (
                  <Button
                    variant="destructive"
                    size="xs"
                    loading={fetchingId === r.id}
                    disabled={fetchingId !== null}
                    onClick={() => void openForceTransition(r.id)}
                    data-testid={`f43fc-booking-force-${r.id}`}
                  >
                    {t('booking:action.force_transition')}
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      </Card>
      {active ? (
        <ForceTransitionDialog
          booking={active}
          open
          onClose={() => setActive(null)}
          onConfirm={handleConfirm}
        />
      ) : null}
    </>
  );
}
